import {
  CHATBOT_WIDGET_ENABLED,
  CHATBOT_ORGANIZATION_ID,
  CHATBOT_WIDGET_ID,
} from "config";

/*******************
  @Purpose : OpenWidget chatbot controller shared by every chatbot button.
             Buttons that should show the chatbot on a page call
             registerChatbotLauncher() on mount; the widget is loaded while at
             least one launcher is mounted and hidden once none are left.
  *******************/

let widgetLoaded = false;
let widgetShown = false;
let launcherCount = 0;

export const isChatbotEnabled = () =>
  Boolean(CHATBOT_WIDGET_ENABLED && CHATBOT_ORGANIZATION_ID && CHATBOT_WIDGET_ID);

const loadChatbotWidget = () => {
  if (widgetLoaded || typeof window === "undefined" || !isChatbotEnabled()) {
    return;
  }

  window.__ow = {
    ...window.__ow,
    organizationId: CHATBOT_ORGANIZATION_ID,
    template_id: CHATBOT_WIDGET_ID,
    integration_name: "manual_settings",
    product_name: "chatbot",
  };

  (function (win, doc, slice) {
    function dispatch(method, args) {
      return widget._h
        ? widget._h.apply(null, [method, args])
        : widget._q.push([method, args]);
    }

    const widget = {
      _q: [],
      _h: null,
      _v: "2.0",
      on: function () { dispatch("on", slice.call(arguments)); },
      once: function () { dispatch("once", slice.call(arguments)); },
      off: function () { dispatch("off", slice.call(arguments)); },
      get: function () {
        if (!widget._h) {
          throw new Error("[OpenWidget] You can't use getters before load.");
        }
        return dispatch("get", slice.call(arguments));
      },
      call: function () { dispatch("call", slice.call(arguments)); },
      init: function () {
        const script = doc.createElement("script");
        script.async = true;
        script.type = "text/javascript";
        script.src = "https://cdn.openwidget.com/openwidget.js";
        doc.head.appendChild(script);
      },
    };

    if (!win.__ow.asyncInit) widget.init();
    win.OpenWidget = win.OpenWidget || widget;
  })(window, document, [].slice);

  // The widget's own launcher is never used; our buttons open it instead.
  window.OpenWidget.call("hide");
  widgetLoaded = true;
};

export const hideChatbot = () => {
  if (widgetLoaded && widgetShown) {
    window.OpenWidget.call("hide");
  }
  widgetShown = false;
};

export const openChatbot = () => {
  if (!isChatbotEnabled()) {
    return;
  }
  loadChatbotWidget();
  window.OpenWidget.call("maximize");
  widgetShown = true;
};

/*******************
  @Purpose : Hide the widget on pages where no launcher is mounted
             (e.g. after it was opened from the footer "Ask" button)
  *******************/
export const syncChatbotVisibility = () => {
  if (launcherCount === 0) {
    hideChatbot();
  }
};

/*******************
  @Purpose : Mark a chatbot button as present on the page; returns the cleanup
  *******************/
export const registerChatbotLauncher = () => {
  if (!isChatbotEnabled()) {
    return () => {};
  }
  launcherCount += 1;
  loadChatbotWidget();

  return () => {
    launcherCount = Math.max(0, launcherCount - 1);
    syncChatbotVisibility();
  };
};
